import React from 'react';
import {
  MenuItem as MuiMenuItem,
  MenuItemProps as MuiMenuItemProps,
  useTheme,
} from '@mui/material';

export interface MenuItemProps extends MuiMenuItemProps {
  /** Whether the current item is currently selected */
  selected?: boolean;

  /** Whether the particular item should be disabled */
  disabled?: boolean;

  /** Whether the item is nested in a group or not */
  nested?: boolean;

  /** The background color of the menu item (overrides theme default) */
  backgroundColor?: string;

  /** The background color when the item is selected (overrides theme default) */
  selectedBackgroundColor?: string;

  /** Whether a check mark icon should be displayed when the item is selected */
  withCheckMark?: boolean;

  /** @ignore */
  children: React.ReactNode;
}

/**
 * A MenuItem is simply an entry in a list of menu options or dropdown options. In general, this
 * should be used only on autocompletes, comboboxes & menus.
 */
const MenuItem = React.forwardRef<HTMLLIElement, MenuItemProps>(function MenuItem(
  {
    selected,
    disabled,
    nested,
    backgroundColor,
    selectedBackgroundColor,
    withCheckMark,
    children,
    ...rest
  },
  ref,
) {
  const theme = useTheme();
  const componentTokens = theme.palette?.componentTokens;

  // Build custom sx styles only for overrides and checkmark functionality
  const customSx = {
    // Override background colors if provided
    ...(backgroundColor && { backgroundColor }),
    ...(selectedBackgroundColor &&
      selected && {
        backgroundColor: selectedBackgroundColor,
        '&:hover': {
          backgroundColor: selectedBackgroundColor,
        },
      }),
    // Checkmark functionality
    ...(withCheckMark && {
      paddingRight: '224px',
      '&::after': {
        content: selected
          ? `url( 'data:image/svg+xml; utf8,<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 20 18" fill="white"><path d="M7 14.17L2.83 10l-1.41 1.41L7 17 19 5l-1.41-1.42L7 14.17z" /></svg>' )`
          : `""`,
        border: '1px solid',
        backgroundColor: componentTokens.selectors.colors.checkbox.selected.background.resting,
        borderColor: componentTokens.selectors.colors.checkbox.selected.stroke.resting,
        borderRadius: '2px',
        position: 'absolute',
        width: '16px',
        height: '16px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        top: 0,
        right: '16px',
        bottom: 0,
        margin: 'auto 0',
      },
    }),
  };

  return (
    <MuiMenuItem
      ref={ref}
      selected={selected}
      disabled={disabled}
      data-nested={nested}
      sx={customSx}
      {...rest}
    >
      {children}
    </MuiMenuItem>
  );
});

export default React.memo(MenuItem);
